import { Component, OnInit, Inject, OnDestroy, ViewChild } from "@angular/core";
import {
  MatDialogRef,
  MAT_DIALOG_DATA,
  MatDialog,
} from "@angular/material/dialog";
import { ActivatedRoute, Router } from "@angular/router";

import { Subscription } from "rxjs";

import {
  TableService,
  TableSchema,
  TableTypes,
  DataTableComponentBase,
  DataTableComponent,
  Filter,
} from "@kortobaa-front/k-ng-datatable";

import { AuthService } from "src/app/core/auth.service";
import { RolesId, UserRoles } from "src/app/shared/models/interfaces";
import { getDate } from "src/app/shared/helpers/convertDate";
import { FilterUsersDialogComponent } from "../dialogs/filter-users-dialog/filter-users-dialog.component";
import * as moment from "moment-timezone";

@Component({
  selector: "app-blacklisted-users",
  templateUrl: "./blacklisted-users.component.html",
  styleUrls: ["./blacklisted-users.component.scss"],
})
export class BlacklistedUsersComponent
  extends DataTableComponentBase
  implements OnInit, OnDestroy
{
  readonly UserRoles = UserRoles;
  moduleName = "users";
  url: string;
  isSearching: boolean = false;

  displayedColumns: TableSchema[] = [
    {
      title: "id",
      id: "id",
      type: TableTypes.number,
      editable: true,
    },
    {
      title: "username",
      id: "username",
      type: TableTypes.text,
      editable: true,
    },
    {
      title: "isSaudi",
      id: "isSaudi",
      type: TableTypes.booleanNumber,
      editable: true,
      notOnFilter: true,
    },
    {
      title: "blacklist-date",
      id: "blacklistDate",
      type: TableTypes.text,
      editable: true,
      notOnFilter: true,
    }, 
    {
      title: "blacklist-reason",
      id: "blacklistReason",
      type: TableTypes.text,
      editable: true,
      notOnFilter: true,
    }, 
    {
      title: "service_name",
      id: "serviceName",
      type: TableTypes.text,
      editable: true,
      notOnFilter: true,
    }, 
  ];
  dataSource;
  isDataLoading = false;

  userRole: UserRoles;
  @ViewChild(DataTableComponent)
  private _dataTableComponent: DataTableComponent;
  dialogSub$!: Subscription;
  filterUsers!: any;
  filterUsersObj: any = {};

  filters: any = {
    limit: 10,
    offset: 0,
    order: ["created_at DESC"],
    where: {
      and: [{ blackListed: true }],
    },
  };
  constructor(
    public authService: AuthService,
    private _tableService: TableService,
    private _dialog: MatDialog,
    private _activatedRoute: ActivatedRoute,
    private _router: Router
  ) {
    super();

    this.url = "user-management";

    this.displayedColumns.push({
      title: `user-data`,
      id: "test",
      type: TableTypes.extrnalinfo,
      notOnFilter: true,
    });
  }

  async ngOnInit() {}

  async getData(filters?: Filter) {
    this.isDataLoading = true;
    this._tableService.tableUrl = `admin/usersByRole/user`;

    if (
      !filters.where.and ||
      filters.where.and.length === 0 ||
      filters.where.and.every((obj) => Object.keys(obj).length === 0)
    ) {
      // Override if it's empty or contains only empty objects
      filters.where.and = [{ blackListed: true }];
    } else {
      filters.where.and.push({ blackListed: true });
    }
    try {
      this.dataSource = (
        await this._tableService.getData<any>(filters).toPromise()
      ).map((user) => ({
        ...user,
        secondName: user.additionalData.secondName,
        thirdName: user.additionalData.thirdName,
        lastName: user.additionalData.lastName,
        isDisabled: !user.isDisabled,
        birthDate: user.additionalData.birthdate
          ? getDate(user.additionalData.birthdate)
          : "",
        totalWorkedHours: Math.trunc(user.totalWorkedMinutes / 60),
        isSaudi: !!user.nid && user.nid.startsWith("1"),
        blacklistDate: moment(new Date(user.additionalData.blackListedAt)).format("LL"),
        blacklistReason: user.additionalData.blackListedReason,
        serviceName: user.additionalData.blackListService ?? "-",
      }));

      if (
        Object.keys(this._dataTableComponent.backendFilter.where.and[0])
          .length !== 0 ||
        this._dataTableComponent.backendFilter.where.and.length >= 1
      ) {
        this._dataTableComponent.tableDataLength = (
          await this._tableService
            .getDataCount(this._dataTableComponent.backendFilter.where)
            .toPromise()
        ).count;
      }
    } catch (error) {
      console.error(error);
    }
    this.isDataLoading = false;
  }

  async getDataForAction(data) {
    delete data["birthDate"];

    this._tableService.tableUrl = `/users`;

    this._tableService.SaveItem({ ...data, roleId: RolesId["user"] }).subscribe(
      (_) => {
        this._tableService.redirect(this.url);
      },
      (error) => {
        alert(error.error.error.message);
      }
    );
  }

  async handleSearchChange(value: string) {
    // TODO: refactor all this logic, it's just a workaround to manipulate the data table component directly because of the poor API
    this.isDataLoading = true;
    if (!value) this._dataTableComponent.paginator.firstPage();

    const filter = {
      or: [
        { username: { like: `${value}` } },
        { phone: { like: `${value}` } },
        { email: { like: `${value}` } },
        { nid: { like: `${value}` } },
      ],
    };

    let roleId = 0;

    roleId = RolesId.user;

    const role = {
      roleId: roleId,
    };

    this._dataTableComponent.backendFilter.where.and[1] = filter;

    this._dataTableComponent.backendFilter.where.and[2] = role;

    this.getData(this._dataTableComponent.backendFilter);

    this._dataTableComponent.tableDataLength = (
      await this._tableService
        .getDataCount(this._dataTableComponent.backendFilter.where)
        .toPromise()
    ).count;

    this.isDataLoading = false;
  }

  showUserData({ data }) {
    if (data) {
      this._router.navigate(["show-user", "user", data.id], {
        queryParams: { userName: data.username },
      });
    } else {
      this.handleCustomFilter("customFilter");
    }
  }

  // handle filter dialog
  async handleCustomFilter(data: any) {
    if (data == "customFilter") {
      this.dialogSub$ = this._dialog
        .open(FilterUsersDialogComponent, {
          direction: "rtl",
          panelClass: "save-case-dialog",
          data: { displayData: this.displayedColumns, list: "blacklisted-users" },
        })
        .afterClosed()
        .subscribe(async (filterUsers: any) => {
          if (filterUsers) {
            this.isDataLoading = true;
            this.handleFilter(filterUsers);
          }
        });
    }
  }

  // handle filteration
  async handleFilter(obj: any) {
    this.filterUsers = obj;
    let body: any = { and: [] };
    if (obj.dateToValue && obj.dateFromValue) {
      body.and = [
        { blackListedAt: { lt: obj.dateToValue } },
        { blackListedAt: { gt: obj.dateFromValue } },
      ];
    }

    for (let item of body.and) {
      if (item.blackListedAt) {
        if (item.blackListedAt.lt) {
          this.filterUsersObj.dateToValue = item.blackListedAt.lt;
        }
        if (item.blackListedAt.gt) {
          this.filterUsersObj.dateFromValue = item.blackListedAt.gt;
        }
      }
    }

    this.filters["where"] = {
      ...this.filters["where"],
      ...body,
    };

    this._dataTableComponent.backendFilter = this.filters;
    this.getData(this.filters);
    this._dataTableComponent.tableDataLength = (
      await this._tableService.getDataCount(this.filters["where"]).toPromise()
    ).count;
    this.isDataLoading = false;
  }

  //remove filter - type and update filter and display it from api
  removeFilter(value: any) {
    if (value == "dateFromValue" || value == "dateToValue") {
      delete this.filterUsersObj["dateFromValue"];
      delete this.filterUsersObj["dateToValue"];
      delete this.filterUsers["dateFromValue"];
      delete this.filterUsers["dateToValue"];
    } else {
      delete this.filterUsersObj[value];
      delete this.filterUsers[value];
    }
    if (Object.keys(this.filterUsersObj).length === 0) {
      this._router.navigateByUrl("/", { skipLocationChange: true }).then(() => {
        this._router.navigate(["user-management/blacklist"]);
      });
    } else {
      this.handleFilter(this.filterUsers);
    }
  }

  backToUsers() {
    this._router.navigate(["user-management"]);
  }

  ngOnDestroy(): void {
    this.dialogSub$?.unsubscribe();
  }
}
